// Category caching service for frontend
export class CategoryCacheService {
  private static readonly CACHE_KEY = 'rapidfix_categories';
  private static readonly SUBCATEGORIES_KEY = 'rapidfix_subcategories';
  private static readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
  private static readonly CACHE_VERSION = '1.0';

  // ---------- Safe localStorage access ----------
  private static getStorage(): Storage | null {
    try {
      if (typeof window === 'undefined') return null;
      return window.localStorage;
    } catch {
      return null;
    }
  }

  // ---------- Categories ----------
  static setCachedCategories(categories: any[]): void {
    const storage = this.getStorage();
    if (!storage || !Array.isArray(categories)) return;

    try {
      const cacheData = {
        data: categories,
        timestamp: Date.now(),
        version: this.CACHE_VERSION
      };
      storage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to cache categories:', error);
    }
  }

  static getCachedCategories(allowExpired: boolean = false): any[] | null {
    const storage = this.getStorage();
    if (!storage) return null;

    try {
      const cached = storage.getItem(this.CACHE_KEY);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);

      // Version check
      if (cacheData.version !== this.CACHE_VERSION) {
        storage.removeItem(this.CACHE_KEY);
        return null;
      }

      // Data validation
      if (!Array.isArray(cacheData.data)) {
        storage.removeItem(this.CACHE_KEY);
        return null;
      }

      const now = Date.now();
      if (!allowExpired && now - cacheData.timestamp > this.CACHE_DURATION) {
        storage.removeItem(this.CACHE_KEY);
        return null;
      }

      return cacheData.data;
    } catch (error) {
      storage.removeItem(this.CACHE_KEY);
      console.warn('Failed to read category cache:', error);
      return null;
    }
  }

  // ---------- Subcategories ----------
  static setCachedSubcategories(categoryId: string, subcategories: any[]): void {
    const storage = this.getStorage();
    if (!storage || !categoryId || !Array.isArray(subcategories)) return;

    try {
      const cached = storage.getItem(this.SUBCATEGORIES_KEY);
      const cacheData = cached ? JSON.parse(cached) : {};

      // Prevent unbounded growth
      const keys = Object.keys(cacheData);
      if (keys.length >= 20 && !cacheData[categoryId]) {
        delete cacheData[keys[0]];
      }

      cacheData[categoryId] = {
        data: subcategories,
        timestamp: Date.now(),
        version: this.CACHE_VERSION
      };

      storage.setItem(this.SUBCATEGORIES_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to cache subcategories:', error);
    }
  }

  static getCachedSubcategories(
    categoryId: string,
    allowExpired: boolean = false
  ): any[] | null {
    const storage = this.getStorage();
    if (!storage || !categoryId) return null;

    try {
      const cached = storage.getItem(this.SUBCATEGORIES_KEY);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      const categoryCache = cacheData[categoryId];

      if (
        !categoryCache ||
        categoryCache.version !== this.CACHE_VERSION ||
        !Array.isArray(categoryCache.data)
      ) {
        delete cacheData[categoryId];
        storage.setItem(this.SUBCATEGORIES_KEY, JSON.stringify(cacheData));
        return null;
      }

      const now = Date.now();
      if (!allowExpired && now - categoryCache.timestamp > this.CACHE_DURATION) {
        delete cacheData[categoryId];
        storage.setItem(this.SUBCATEGORIES_KEY, JSON.stringify(cacheData));
        return null;
      }

      return categoryCache.data;
    } catch (error) {
      storage.removeItem(this.SUBCATEGORIES_KEY);
      console.warn('Failed to read subcategory cache:', error);
      return null;
    }
  }

  // ---------- Utilities ----------
  static clearCache(): void {
    const storage = this.getStorage();
    if (!storage) return;

    storage.removeItem(this.CACHE_KEY);
    storage.removeItem(this.SUBCATEGORIES_KEY);
  }

  static getCacheInfo(): { categories: any; subcategories: any } {
    const storage = this.getStorage();
    if (!storage) {
      return { categories: null, subcategories: null };
    }

    try {
      return {
        categories: JSON.parse(storage.getItem(this.CACHE_KEY) || 'null'),
        subcategories: JSON.parse(storage.getItem(this.SUBCATEGORIES_KEY) || 'null')
      };
    } catch {
      return { categories: null, subcategories: null };
    }
  }
}